/**
 * scheduleDay.js — days on the Today schedule.
 *
 * The schedule (widgets/TodayScheduleWidget.js) can show any day. Anything that
 * creates a block on another day — the brief's "Block 30 min" button, the AI
 * chat's chips — says where it went ("Added to tomorrow, 9:00") and can open
 * the schedule on that day with showScheduleDay(date).
 */

/** Local YYYY-MM-DD (never UTC — avoids off-by-one around midnight). */
export const localDateStr = (d = new Date()) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const parse = (ymd) => {
  const [y, m, d] = String(ymd).split('-').map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
};

export const addDays = (ymd, n) => {
  const d = parse(ymd);
  d.setDate(d.getDate() + n);
  return localDateStr(d);
};

/** 'Today' / 'Tomorrow' / 'Yesterday' / 'Sat 18 Oct' */
export const dayLabel = (ymd, today = localDateStr()) => {
  if (!ymd) return '';
  if (ymd === today) return 'Today';
  if (ymd === addDays(today, 1)) return 'Tomorrow';
  if (ymd === addDays(today, -1)) return 'Yesterday';
  return parse(ymd).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
};

/** '9:00' / '14:30' → '9:00' / '2:30 PM'-free short 24h-ish label used in confirmations. */
const clock = (hhmm) => {
  if (!hhmm) return '';
  const [h, m] = String(hhmm).split(':').map(Number);
  return `${h}:${String(m || 0).padStart(2, '0')}`;
};

/** 'Added to tomorrow, 9:00' / 'Added to today, 14:30' / 'Added to Sat 18 Oct, 9:00' */
export const blockAddedLabel = (ymd, start, today = localDateStr()) => {
  const day = dayLabel(ymd, today);
  const name = day === 'Today' || day === 'Tomorrow' || day === 'Yesterday' ? day.toLowerCase() : day;
  return `Added to ${name}${start ? `, ${clock(start)}` : ''}`;
};

/**
 * Open the schedule on a day. Works whether or not the schedule is on screen:
 * a mounted schedule listens for the event; one that mounts next (after a tab
 * switch) picks the day up from consumePendingScheduleDay().
 */
export const showScheduleDay = (ymd) => {
  if (!ymd || typeof window === 'undefined') return;
  window.__fpPendingScheduleDay = ymd;
  window.dispatchEvent(new CustomEvent('fp:showScheduleDay', { detail: { date: ymd } }));
};

export const consumePendingScheduleDay = () => {
  if (typeof window === 'undefined') return null;
  const d = window.__fpPendingScheduleDay || null;
  window.__fpPendingScheduleDay = null;
  return d;
};
